import React, { useState, useEffect } from 'react';
import Popover from '@mui/material/Popover';
import './Viewer.css';
import DOMPurify from 'dompurify';
import ComponentTitle from '../ComponentTitle';

export type HotspotItem = {
  url: string;
  hotspots: Hotspot[];
};

export type Hotspot = {
  x: number;
  y: number;
  color?: {
    transparent: Boolean;
    hex: string;
  };
  description: string;
};

interface ViewerProps {
  element: any | undefined;
}

const Viewer: React.FC<ViewerProps> = ({ element }) => {
  const baseColor = '#97ff97';
  const [anchorEl, setAnchorEl] = useState<HTMLDivElement | null>(null);
  const [clickedHotspot, setClickedHotspot] = useState<Hotspot | null>(null);
  const [hotspotColor, setHotspotColor] = useState<string>(baseColor);

  const hotSpotItem = JSON.parse(element.hotspots.value) as HotspotItem;
  const hotspotTemp =
    (JSON.parse(element.hotspots.value).hotspots as Hotspot[]) || [];
  useEffect(() => {
    if (element.visibility.value && element.visibility.value.length > 0) {
      if (
        element.visibility.value[0].name.toString().toLowerCase() ===
        'non-visible'
      ) {
        setHotspotColor(baseColor + '00'); // fully transparent
      } else {
        setHotspotColor(baseColor + 'DD'); // semi-transparent
      }
    }
  }, [element.visibility.value]);

  // Close popover on scroll
  useEffect(() => {
    const handleScroll = (): void => {
      if (anchorEl) {
        handlePopoverClose();
      }
    };

    window.addEventListener('scroll', handleScroll, true);
    return () => {
      window.removeEventListener('scroll', handleScroll, true);
    };
  }, [anchorEl]);
  // Updated to handle both mouse and touch events
  const handleHotspotClick = (
    event: React.MouseEvent<HTMLDivElement> | React.TouchEvent<HTMLDivElement>,
    hotspot: Hotspot,
  ): void => {
    setAnchorEl(event.currentTarget);
    setClickedHotspot(hotspot);
  };

  const handlePopoverClose = (): void => {
    setAnchorEl(null);
    setClickedHotspot(null);
  };

  return (
    <div
      style={{
        width: '100%',
        height: '100%',
        alignContent: 'center',
        justifyContent: 'center',
        display: 'flex',
      }}
    >
      <div className="viewer-container da-panel-primary">
        {element.title?.value && <ComponentTitle title={element.title.value} />}
        <img
          src={hotSpotItem.url}
          alt="Hotspot Viewer"
          className="viewer-image"
        />
        {hotspotTemp.length > 0 &&
          hotspotTemp.map((hotspot, index) => (
            <div
              key={index}
              className="hotspot"
              style={{
                top: `${hotspot.y - 1}%`,
                left: `${hotspot.x}% `,
                backgroundColor: `${hotspotColor}`,
              }}
              onClick={(event): void => handleHotspotClick(event, hotspot)}
              onMouseOver={(event): void => handleHotspotClick(event, hotspot)}
              onTouchStart={(event): void => {
                event.preventDefault(); // Prevent default touch behavior
                // Only register touch, don't trigger click yet
              }}
              onTouchEnd={(event): void => {
                event.preventDefault(); // Prevent default touch behavior
                handleHotspotClick(
                  event as unknown as React.MouseEvent<HTMLDivElement>,
                  hotspot,
                );
              }}
            />
          ))}
        <Popover
          open={Boolean(anchorEl)}
          anchorEl={anchorEl}
          onClose={handlePopoverClose}
          disablePortal
          disableScrollLock
          anchorOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
          transformOrigin={{
            vertical: 'bottom',
            horizontal: 'center',
          }}
          slotProps={{
            paper: {
              style: {
                pointerEvents: 'auto',
                position: 'absolute',
                zIndex: 1300,
              },
            },
          }}
        >
          {clickedHotspot && (
            <div className="tooltip-inner">
              <div
                dangerouslySetInnerHTML={{
                  __html: DOMPurify.sanitize(clickedHotspot.description || ''),
                }}
              />
            </div>
          )}
        </Popover>
      </div>
    </div>
  );
};

export default Viewer;
